// SPDX-License-Identifier: AGPL-3.0-or-later

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import Authentication from '@app/features/auth/state/Authentication';
import * as ChannelPinCommands from '@app/features/channel/commands/ChannelPinsCommands';
import {useMaybeMessageViewContext} from '@app/features/channel/components/MessageViewContext';
import type {Channel} from '@app/features/channel/models/Channel';
import Channels from '@app/features/channel/state/Channels';
import {isSystemDmChannel} from '@app/features/channel/utils/ChannelUtils';
import type {UnicodeEmoji} from '@app/features/emoji/types/EmojiTypes';
import Guilds from '@app/features/guild/state/Guilds';
import GuildVerification from '@app/features/guild/state/GuildVerification';
import GuildMembers from '@app/features/member/state/GuildMembers';
import * as MessageCommands from '@app/features/messaging/commands/MessageCommands';
import * as ReactionCommands from '@app/features/messaging/commands/ReactionCommands';
import * as SavedMessageCommands from '@app/features/messaging/commands/SavedMessageCommands';
import {ForwardModal} from '@app/features/messaging/components/modals/ForwardModal';
import type {Message} from '@app/features/messaging/models/Message';
import SavedMessages from '@app/features/messaging/state/SavedMessages';
import {buildMessageJumpLink} from '@app/features/messaging/utils/MessageLinkUtils';
import {retryFailedMessage} from '@app/features/messaging/utils/MessageRetryUtils';
import {type ReactionEmoji, toReactionEmoji} from '@app/features/messaging/utils/ReactionUtils';
import {getDefaultReplyMention} from '@app/features/notification/utils/MentionReplyPreferenceUtils';
import Permission from '@app/features/permissions/state/Permission';
import {ComponentDispatch} from '@app/features/platform/utils/ComponentBus';
import * as ReadStateCommands from '@app/features/read_state/commands/ReadStateCommands';
import Relationships from '@app/features/relationship/state/Relationships';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as TextCopyCommands from '@app/features/ui/commands/TextCopyCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import Users from '@app/features/user/state/Users';
import TtsUtils from '@app/features/voice/utils/TtsUtils';
import {
	isMessageTypeDeletable,
	MessageFlags,
	MessageStates,
	MessageTypes,
	Permissions,
} from '@app/vendor/constants/ChannelConstants';
import {GuildOperations} from '@app/vendor/constants/GuildConstants';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';
export function getEffectiveContent(message: Message): string {
	if (message.content) return message.content;
	if (message.messageSnapshots && message.messageSnapshots.length > 0) {
		return message.messageSnapshots[0].content ?? '';
	}
	return '';
}
export function isEmbedsSuppressed(message: Message): boolean {
	return (message.flags & MessageFlags.SUPPRESS_EMBEDS) !== 0;
}
export function isClientSystemMessage(message: Message): boolean {
	return message.isClientSystemMessage();
}
export function canReportMessage(message: Message): boolean {
	if (message.state !== MessageStates.SENT) {
		return false;
	}
	if (message.isCurrentUserAuthor()) {
		return false;
	}
	if (message.author.system) {
		return false;
	}
	return message.type === MessageTypes.DEFAULT || message.type === MessageTypes.REPLY;
}
export function canDeleteAttachmentUtil(message: Message | undefined): boolean {
	if (!message?.isCurrentUserAuthor()) return false;
	const channel = Channels.getChannel(message.channelId);
	const guild = channel?.guildId ? Guilds.getGuild(channel.guildId) : null;
	const sendMessageDisabled = guild ? (guild.disabledOperations & GuildOperations.SEND_MESSAGE) !== 0 : false;
	return !sendMessageDisabled;
}
export function requestOpenReactionPicker(messageId: string): void {
	ComponentDispatch.dispatch('EMOJI_PICKER_OPEN', {messageId});
}
export function triggerAddReaction(message: Message): boolean {
	if (isClientSystemMessage(message)) {
		return false;
	}
	const messageElement = document.querySelector<HTMLElement>(`[data-message-id="${message.id}"]`);
	if (!messageElement) {
		ComponentDispatch.dispatch('EMOJI_PICKER_OPEN', {messageId: message.id});
		return false;
	}
	const addReactionButton = messageElement.querySelector<HTMLButtonElement>(
		'[data-action="message-add-reaction-button"]',
	);
	if (!addReactionButton) {
		ComponentDispatch.dispatch('EMOJI_PICKER_OPEN', {messageId: message.id});
		return false;
	}
	addReactionButton.click();
	return true;
}
export interface MessagePermissions {
	channel: Channel;
	isDM: boolean;
	canSendMessages: boolean;
	canAddReactions: boolean;
	canEditMessage: boolean;
	canDeleteMessage: boolean;
	canDeleteAttachment: boolean;
	canPinMessage: boolean;
	canSuppressEmbeds: boolean;
	shouldRenderSuppressEmbeds: boolean;
}
function getMessagePermissionsFromState(message: Message): MessagePermissions | null {
	const channel = Channels.getChannel(message.channelId);
	if (!channel) {
		return null;
	}
	const isDM = !channel.guildId;
	const isAuthorBlocked = Relationships.isBlocked(message.author.id);
	const interactionsBlocked = isSystemDmChannel(channel);
	const isClientSystem = isClientSystemMessage(message);
	const passesVerification = isDM || GuildVerification.canAccessGuild(channel.guildId || '');
	const guild = channel.guildId ? Guilds.getGuild(channel.guildId) : null;
	const sendMessageDisabled = guild ? (guild.disabledOperations & GuildOperations.SEND_MESSAGE) !== 0 : false;
	const reactionsDisabled = guild ? (guild.disabledOperations & GuildOperations.REACTIONS) !== 0 : false;
	const messageTypeDeletable = isMessageTypeDeletable(message.type);
	const currentUserId = Authentication.currentUserId;
	const isCurrentUserTimedOut = guild && currentUserId ? GuildMembers.isUserTimedOut(guild.id, currentUserId) : false;
	const canSendMessages =
		!isClientSystem &&
		!interactionsBlocked &&
		(isDM ||
			(!sendMessageDisabled &&
				Permission.can(Permissions.SEND_MESSAGES, {channelId: message.channelId}) &&
				passesVerification));
	const canAddReactions =
		!isClientSystem &&
		!interactionsBlocked &&
		!isAuthorBlocked &&
		(isDM ||
			(!reactionsDisabled &&
				Permission.can(Permissions.ADD_REACTIONS, {channelId: message.channelId}) &&
				passesVerification &&
				!isCurrentUserTimedOut));
	const canEditMessage = !interactionsBlocked && !sendMessageDisabled && message.isCurrentUserAuthor();
	const canDeleteMessage =
		!interactionsBlocked &&
		messageTypeDeletable &&
		!sendMessageDisabled &&
		(message.isCurrentUserAuthor() ||
			(isDM ? false : Permission.can(Permissions.MANAGE_MESSAGES, {channelId: message.channelId})));
	const canDeleteAttachment = !interactionsBlocked && !sendMessageDisabled && message.isCurrentUserAuthor();
	const canPinMessage =
		!interactionsBlocked &&
		!sendMessageDisabled &&
		(isDM ? true : Permission.can(Permissions.PIN_MESSAGES, {channelId: message.channelId}));
	const canSuppressEmbeds =
		!interactionsBlocked &&
		!sendMessageDisabled &&
		message.isUserMessage() &&
		(message.isCurrentUserAuthor() || (!isDM && canDeleteMessage));
	const shouldRenderSuppressEmbeds =
		message.isUserMessage() && canSuppressEmbeds && (isEmbedsSuppressed(message) || message.embeds.length > 0);
	return {
		channel,
		isDM,
		canSendMessages,
		canAddReactions,
		canEditMessage,
		canDeleteMessage,
		canDeleteAttachment,
		canPinMessage,
		canSuppressEmbeds,
		shouldRenderSuppressEmbeds,
	};
}
export function useMessagePermissions(message: Message): MessagePermissions | null {
	const context = useMaybeMessageViewContext();
	if (context?.previewPermissions) {
		return {
			channel: context.channel,
			...context.previewPermissions,
		};
	}
	return getMessagePermissionsFromState(message);
}
interface MessageActionHandlersOptions {
	i18n: I18n;
	onClose?: () => void;
}
export interface MessageActionHandlers {
	handleEmojiSelect: (emoji: UnicodeEmoji | ReactionEmoji) => void;
	handleCopyMessageId: () => void;
	handleCopyMessage: () => void;
	handleCopyMessageLink: () => void;
	handleSaveMessage: (isSaved: boolean) => (event?: React.MouseEvent | React.KeyboardEvent) => void;
	handleToggleSuppressEmbeds: () => void;
	handleReply: (event?: React.MouseEvent | React.KeyboardEvent) => void;
	handlePinMessage: (event?: React.MouseEvent | React.KeyboardEvent) => void;
	handleEditMessage: () => void;
	handleRetryMessage: () => void;
	handleFailedMessageDelete: () => void;
	handleForward: () => void;
	handleRemoveAllReactions: () => void;
	handleMarkAsUnread: () => void;
}
export function createMessageActionHandlers(
	message: Message,
	options: MessageActionHandlersOptions,
): MessageActionHandlers {
	const {i18n, onClose} = options;
	const handleEmojiSelect = (emoji: UnicodeEmoji | ReactionEmoji) => {
		if (isClientSystemMessage(message)) {
			return;
		}
		ReactionCommands.addReaction(i18n, message.channelId, message.id, toReactionEmoji(emoji));
	};
	const handleCopyMessageId = () => {
		requestCopyMessageId(message, i18n);
		onClose?.();
	};
	const handleCopyMessage = () => {
		const content = getEffectiveContent(message);
		if (content) {
			TextCopyCommands.copy(i18n, content);
			onClose?.();
		}
	};
	const handleCopyMessageLink = () => {
		requestCopyMessageLink(message, i18n);
		onClose?.();
	};
	const handleSaveMessage = (isSaved: boolean) => () => {
		if (isClientSystemMessage(message)) {
			onClose?.();
			return;
		}
		if (isSaved) {
			SavedMessageCommands.remove(i18n, message.id);
		} else {
			void SavedMessageCommands.create(i18n, message.channelId, message.id);
		}
		onClose?.();
	};
	const handleToggleSuppressEmbeds = () => {
		requestToggleSuppressEmbeds(message, i18n);
		onClose?.();
	};
	const handleReply = (event?: React.MouseEvent | React.KeyboardEvent) => {
		const channel = Channels.getChannel(message.channelId)!;
		requestMessageReply(message, {
			mention: !event?.shiftKey && !message.isCurrentUserAuthor() && channel.guildId != null,
		});
		onClose?.();
	};
	const handlePinMessage = (event?: React.MouseEvent | React.KeyboardEvent) => {
		onClose?.();
		requestMessagePin(message, i18n, {shiftKey: Boolean(event?.shiftKey)});
	};
	const handleEditMessage = () => {
		startMessageEdit(message);
		onClose?.();
	};
	const handleRetryMessage = () => {
		retryFailedMessage(message);
		onClose?.();
	};
	const handleFailedMessageDelete = () => {
		MessageCommands.deleteLocal(message.channelId, message.id);
		onClose?.();
	};
	const handleForward = () => {
		onClose?.();
		requestMessageForward(message);
	};
	const handleRemoveAllReactions = () => {
		onClose?.();
		requestRemoveAllReactions(message, i18n);
	};
	const handleMarkAsUnread = () => {
		requestMarkMessageUnread(message);
		onClose?.();
	};
	return {
		handleEmojiSelect,
		handleCopyMessageId,
		handleCopyMessage,
		handleCopyMessageLink,
		handleSaveMessage,
		handleToggleSuppressEmbeds,
		handleReply,
		handlePinMessage,
		handleEditMessage,
		handleRetryMessage,
		handleFailedMessageDelete,
		handleForward,
		handleRemoveAllReactions,
		handleMarkAsUnread,
	};
}
export function startMessageEdit(message: Message): void {
	if (message.messageSnapshots) {
		return;
	}
	if (MobileLayout.isEnabled()) {
		MessageCommands.startEditMobile(message.channelId, message.id);
	} else {
		MessageCommands.startEdit(message.channelId, message.id, message.content);
	}
}
export function requestDeleteMessage(message: Message, i18n: I18n, bypassConfirm = false): void {
	if (bypassConfirm) {
		MessageCommands.remove(message.channelId, message.id);
		return;
	}
	MessageCommands.showDeleteConfirmation(i18n, {message});
}
export function requestMessagePin(message: Message, i18n: I18n, options: {shiftKey?: boolean} = {}): void {
	if (message.pinned) {
		if (options.shiftKey) {
			ChannelPinCommands.unpin(message.channelId, message.id);
			return;
		}
		ModalCommands.push(
			modal(() => (
				<ConfirmModal
					title={i18n._(msg`Unpin Message`)}
					description={i18n._(msg`Do you want to send this pin back to the future?`)}
					message={message}
					primaryText={i18n._(msg`Unpin it`)}
					onPrimary={() => ChannelPinCommands.unpin(message.channelId, message.id)}
				/>
			)),
		);
		return;
	}
	if (options.shiftKey) {
		ChannelPinCommands.pin(message.channelId, message.id);
		return;
	}
	ModalCommands.push(
		modal(() => (
			<ConfirmModal
				title={i18n._(msg`Pin it. Pin it good.`)}
				description={i18n._(msg`Pin this message to the channel for all to see. Unless ... you're chicken.`)}
				message={message}
				primaryText={i18n._(msg`Pin it real good`)}
				primaryVariant="primary"
				onPrimary={() => ChannelPinCommands.pin(message.channelId, message.id)}
			/>
		)),
	);
}
export function requestRemoveAllReactions(message: Message, i18n: I18n): void {
	ModalCommands.push(
		modal(() => (
			<ConfirmModal
				title={i18n._(msg`Remove All Reactions`)}
				description={i18n._(msg`Are you sure you want to remove all reactions from this message?`)}
				message={message}
				primaryText={i18n._(msg`Remove All`)}
				primaryVariant="danger-primary"
				onPrimary={() => ReactionCommands.removeAllReactions(i18n, message.channelId, message.id)}
			/>
		)),
	);
}
interface RequestMessageReplyOptions {
	mention?: boolean;
}
export function requestMessageReply(message: Message, options?: RequestMessageReplyOptions): void {
	if (isClientSystemMessage(message)) {
		return;
	}
	const channel = Channels.getChannel(message.channelId);
	if (!channel) return;
	const startReply = (mentioning: boolean) => {
		MessageCommands.startReply(message.channelId, message.id, mentioning);
	};
	const shouldMention = getDefaultReplyMention({
		authorId: message.author.id,
		isOwnMessage: message.isCurrentUserAuthor(),
		guildId: channel.guildId,
		fallbackMention: options?.mention,
	});
	startReply(shouldMention);
}
export function requestMessageForward(message: Message): void {
	if (isClientSystemMessage(message)) {
		return;
	}
	const currentUser = Users.currentUser;
	if (!currentUser) {
		return;
	}
	ModalCommands.push(modal(() => <ForwardModal message={message} user={currentUser} />));
}
export function requestCopyMessageText(message: Message, i18n: I18n): void {
	const content = getEffectiveContent(message);
	if (!content) return;
	void TextCopyCommands.copy(i18n, content);
}
export function requestMarkMessageUnread(message: Message): void {
	ReadStateCommands.markAsUnread(message.channelId, message.id);
}
export function requestCopyMessageLink(message: Message, i18n: I18n): void {
	if (isClientSystemMessage(message)) {
		return;
	}
	const channel = Channels.getChannel(message.channelId);
	if (!channel) return;
	const jumpLink = buildMessageJumpLink({
		guildId: channel.guildId,
		channelId: message.channelId,
		messageId: message.id,
	});
	void TextCopyCommands.copy(i18n, jumpLink);
}
export function requestCopyMessageId(message: Message, i18n: I18n): void {
	void TextCopyCommands.copy(i18n, message.id);
}
export function requestToggleBookmark(message: Message, i18n: I18n): void {
	if (isClientSystemMessage(message)) {
		return;
	}
	if (SavedMessages.isSaved(message.id)) {
		SavedMessageCommands.remove(i18n, message.id);
		return;
	}
	void SavedMessageCommands.create(i18n, message.channelId, message.id);
}
export function requestToggleSuppressEmbeds(message: Message, i18n: I18n): void {
	if (isEmbedsSuppressed(message)) {
		MessageCommands.edit(message.channelId, message.id, undefined, message.flags & ~MessageFlags.SUPPRESS_EMBEDS).then(
			() => {
				ToastCommands.createToast({
					type: 'success',
					children: i18n._(msg`Embeds unsuppressed`),
				});
			},
		);
		return;
	}
	MessageCommands.edit(message.channelId, message.id, undefined, message.flags | MessageFlags.SUPPRESS_EMBEDS).then(
		() => {
			ToastCommands.createToast({
				type: 'success',
				children: i18n._(msg`Embeds suppressed`),
			});
		},
	);
}
export function requestSpeakMessage(message: Message): void {
	TtsUtils.speakMessage(getEffectiveContent(message));
}
